import {
  LoginResponseData,
  ResponseCreateCheckoutSession,
  StripeProductsResponseDto,
  SubscriptionDetailsResponseDto,
  UpdateSubscriptionPlanResponse,
} from '@EHSNavigator/backend';
import clsx from 'clsx';
import React from 'react';
import PricingPlansCard from '../PricingPlansCard';
import { useMutation } from '@tanstack/react-query';
import { useApiClients } from '../../client/api/useApiClient';
import styles from './subscription-modal.module.scss';
import { toast } from 'react-toastify';
import Slider from '../Slider';
import { useLocalStorage } from '@uidotdev/usehooks';
import { queryClient } from '../../main';
import { toggleModal } from '../../reducers/modalToggle';
import { useDispatch } from 'react-redux';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';

interface SubscriptionModalProps {
  otherClasses?: string;
  plans: StripeProductsResponseDto['products'];
  data?: SubscriptionDetailsResponseDto;
  refetch: any;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  otherClasses,
  plans,
  refetch,
  data,
}) => {
  const subscriptionModalClasses = clsx(otherClasses);
  const stripeCurrentPlan = data?.subscription?.currentPlan?.subscription || null;
  const manualCurrentPlan = data?.manualSubscription || null;

  const { UserSettingsApi, StripeApi } = useApiClients();
  const [userInfo, setUserInfo] = useLocalStorage<LoginResponseData | null>('userInfo', null);

  const dispatch = useDispatch();

  const mutation = useMutation<UpdateSubscriptionPlanResponse, Error, any>({
    mutationFn: async ({
      newPriceId,
      maxCount,
    }: {
      newPriceId: string;
      maxCount: string;
    }) => {
      const response =
        Number(maxCount) > Number(userInfo?.currentSubscription?.max_count)
          ? await UserSettingsApi?.userSettingsControllerUpgradeSubscription({
            upgradeSubscriptionDto: {
              newPriceId: newPriceId,
            },
            userId: userInfo?.user?.id as string,
          })
          : await UserSettingsApi?.userSettingsControllerDowngradeSubscription({
            upgradeSubscriptionDto: {
              newPriceId: newPriceId,
            },
            userId: userInfo?.user?.id as string,
          });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['get-subscription-details'], (oldData: any) => {
        return {
          ...oldData,
          currentPlan: {
            ...oldData?.currentPlan,
            ...data?.currentPlan,
          },
        };
      });
      setUserInfo((prev: any) => ({
        ...prev,
        currentSubscription: {
          ...prev?.currentSubscription,
          name: data?.currentPlan?.name,
          max_count: data?.currentPlan?.max_count,
        },
      }));
      toast.success(data?.message);
      dispatch(toggleModal(''));
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || error?.response?.data?.error?.message;

      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const reActivateSubscription = useMutation<any, Error, any>({
    mutationFn: async () => {
      const response =
        await StripeApi?.stripeControllerReactivateSubscription();
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['get-subscription-details'], (oldData: any) => {
        return {
          ...oldData,
          currentPlan: {
            ...oldData?.currentPlan,
            subscription: {
              ...data?.subscription,
            },
          },
        };
      });
      toast.success(data?.message);
      dispatch(toggleModal(''));
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || error?.response?.data?.error?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });
  
  const upgradeToProPlan = useMutation<any, Error, any>({
    mutationFn: async () => {
      const response =
        await StripeApi?.stripeControllerUpgradeFromTrialToPaid();
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message);
      queryClient.refetchQueries({ queryKey: ['current-active-plan'] });
      dispatch(toggleModal(''));
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || error?.response?.data?.error?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const currentSubscriptionMutation = useMutation<
    ResponseCreateCheckoutSession,
    Error
  >({
    mutationFn: async () => {
      const response =
        await StripeApi?.stripeControllerCreateCheckoutSessionForCurrentSubscription();
      return response?.data;
    },
    onSuccess: (data) => {
      window.open(data?.session_url, '_blank');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const settings = {
    infinite: true,
    speed: 1000,
    slidesToShow: 2,
    centerMode: true,
    slidesToScroll: 1,
    dots: false,
    centerPadding: '60px',
    arrows: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerPadding: '180px',
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          centerPadding: '80px',
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          centerPadding: '16px',
        },
      },
    ],
  };

  if (manualCurrentPlan) {
    return (
      <div className={subscriptionModalClasses} data-testid="subscription-modal">
        <div className="mb-6 flex flex-col items-center gap-2.5 text-center lg:mb-12">
          <img
            src="/assets/images/logo.png"
            alt="logo"
            className="h-85 w-69 md:h-100 md:w-20"
          />
        </div>
        <div className="mt-10 flex flex-col items-center text-center">
          <WarningIcon className="[&>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
          <h4 className="mt-15 text-brand-byzantine">Manual Subscription Plan</h4>
          <p className="font-inter-variable text-neutral-tones-text mt-4 text-base leading-5 font-normal">
            Your subscription is managed manually. Please contact the team for subscription management and plan changes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={subscriptionModalClasses} data-testid="subscription-modal">
      <div className="mb-6 flex flex-col items-center gap-2.5 text-center lg:mb-12">
        <img
          src="/assets/images/logo.png"
          alt="logo"
          className="h-85 w-69 md:h-100 md:w-20"
        />
        {plans?.length > 1 ? (
          <h2 className="text-brand-byzantine font-bold">Pricing Plans</h2>
        ) : (
          <h2 className="text-brand-byzantine font-bold">Pricing Plan</h2>
        )}
      </div>
      <div
        className={clsx(
          'mx-auto max-w-1512 gap-4 lg:grid',
          plans?.length > 1 ? 'hidden lg:grid-cols-3' : 'lg:grid-cols-1',
        )}
      >
        {plans?.map((node, index) => {
          return (
            <PricingPlansCard
              key={index}
              data={node}
              subscriptionModal
              disable={{
                upgrade:
                  upgradeToProPlan?.isPending ||
                  currentSubscriptionMutation.isPending,
                other: mutation?.isPending || reActivateSubscription?.isPending,
              }}
              type={stripeCurrentPlan?.status}
              otherClasses="w-fit mx-auto"
              loading={{
                other:
                  (mutation?.isPending &&
                    Number(mutation?.variables?.maxCount) ===
                    Number(node?.metadata?.max_count)) ||
                  reActivateSubscription?.isPending,
                upgrade:
                  upgradeToProPlan?.isPending ||
                  currentSubscriptionMutation.isPending,
              }}
              mode={plans?.length > 1 ? 'light' : 'dark'}
              handlePlanClick={(e) => {
                if (e?.type === 'cancel-subscription') {
                  dispatch(toggleModal('cancel-subscription'));
                } else if (e?.type === 're-activate-subscription') {
                  reActivateSubscription?.mutate({});
                } else if (e?.type === 'subscribe') {
                  upgradeToProPlan?.mutate({});
                  refetch();
                } else if (e?.type === 'past-due') {
                  currentSubscriptionMutation.mutate();
                } else {
                  mutation.mutate({
                    newPriceId: e.newPriceId,
                    maxCount: e.maxCount,
                  });
                }
              }}
            />
          );
        })}
      </div>
      <div
        className={clsx(
          'w-full pl-9',
          plans?.length > 1 ? 'block lg:hidden' : 'hidden',
        )}
      >
        <Slider
          customSettings={settings}
          customClass={styles.pricing_plan_slider}
        >
          {plans?.map((node, index) => (
            <div className="pr-9">
              <PricingPlansCard
                key={index}
                data={node}
                disable={{
                  upgrade:
                    upgradeToProPlan?.isPending ||
                    currentSubscriptionMutation.isPending,
                  other:
                    mutation?.isPending || reActivateSubscription?.isPending,
                }}
                type={stripeCurrentPlan?.status}
                loading={{
                  other:
                    (mutation?.isPending &&
                      Number(mutation?.variables?.maxCount) ===
                      Number(node?.metadata?.max_count)) ||
                    reActivateSubscription?.isPending,

                  upgrade:
                    upgradeToProPlan?.isPending ||
                    currentSubscriptionMutation.isPending,
                }}
                subscriptionModal
                handlePlanClick={(e) => {
                  if (e?.type === 'cancel-subscription') {
                    dispatch(toggleModal('cancel-subscription'));
                  } else if (e?.type === 're-activate-subscription') {
                    reActivateSubscription?.mutate({});
                  } else if (e?.type === 'subscribe') {
                    upgradeToProPlan?.mutate({});
                    refetch();
                  } else if (e?.type === 'past-due') {
                    currentSubscriptionMutation.mutate();
                  } else {
                    mutation.mutate({
                      newPriceId: e.newPriceId,
                      maxCount: e.maxCount,
                    });
                  }
                }}
              />
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

export default SubscriptionModal;
